import React from 'react';
import { FileText, Image as ImageIcon, Download, ExternalLink } from 'lucide-react';
import { uploadsApi } from '../services/api';

/**
 * Renders attachments inline within a message bubble.
 * - Images: render thumbnail (clickable to open full size in new tab).
 * - Other files: render filename pill with download icon.
 *
 * Auth is handled via ?auth=<token> query param so <img src> works without headers.
 */
export const MessageAttachments = ({ attachments }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="message-attachments">
      {attachments.map((a) => {
        const url = uploadsApi.downloadUrl(a.id);
        if (a.is_image || (a.content_type || '').startsWith('image/')) {
          return (
            <a
              key={a.id}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
              data-testid={`message-attachment-image-${a.id}`}
              aria-label={`View ${a.original_filename}`}
            >
              <img
                src={url}
                alt={a.original_filename || 'Attachment'}
                className="w-24 h-24 object-cover rounded border border-gray-200 group-hover:ring-2 ring-purple-300 transition"
                loading="lazy"
              />
            </a>
          );
        }
        return (
          <a
            key={a.id}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-md px-2 py-1 text-xs hover:bg-gray-50 hover:border-purple-300 transition group"
            data-testid={`message-attachment-file-${a.id}`}
          >
            <FileText className="w-3.5 h-3.5 text-purple-500" />
            <span className="font-medium text-gray-800 max-w-[180px] truncate">{a.original_filename}</span>
            <span className="text-gray-400">({Math.round((a.size || 0) / 1024)} KB)</span>
            <Download className="w-3 h-3 text-gray-400 group-hover:text-purple-600" />
          </a>
        );
      })}
    </div>
  );
};

export default MessageAttachments;
